/*
  Collection tabs on the collection page.

  Each tab points at a collection. Clicking one fetches that collection through the
  Section Rendering API and swaps only #ProductGridContainer, so the page stays put.
  The URL gets ?tab=<handle> so the choice survives reload, back and forward.
  The "All" tab carries an empty handle, which restores the page's own collection.

  The sidebar facets belong to the page's own collection, not to the tab, so picking a
  facet resets the tabs back to "All" rather than leaving a grid that contradicts them.
*/
class CollectionTabs {
  constructor(root) {
    this.root = root;
    this.sectionId = root.dataset.sectionId;
    this.baseUrl = root.dataset.baseUrl;
    this.tabs = Array.from(root.querySelectorAll('.collection-btn'));
    this.cache = new Map();

    this.tabs.forEach((tab) => tab.addEventListener('click', this.onTabClick.bind(this)));

    window.addEventListener('popstate', () => this.applyHandle(CollectionTabs.handleFromUrl(), false));
    this.watchFacets();

    const initial = CollectionTabs.handleFromUrl();
    if (initial) {
      // Stamp our state onto the entry we landed on, so Back into it behaves like any other tab.
      this.writeUrl(initial, true);
      this.applyHandle(initial, false);
    }
  }

  static handleFromUrl() {
    return new URLSearchParams(window.location.search).get('tab') || '';
  }

  get grid() {
    return document.getElementById('ProductGridContainer');
  }

  onTabClick(event) {
    event.preventDefault();
    const handle = event.currentTarget.dataset.tabHandle || '';
    if (handle === this.activeHandle()) return;
    this.applyHandle(handle, true);
  }

  activeHandle() {
    const active = this.tabs.find((tab) => tab.classList.contains('active'));
    return active ? active.dataset.tabHandle || '' : '';
  }

  markActive(handle) {
    this.tabs.forEach((tab) => {
      const isActive = (tab.dataset.tabHandle || '') === handle;
      tab.classList.toggle('active', isActive);
      tab.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  // A facet change re-renders the grid from the page's own collection, which would leave
  // a tab highlighted over products it does not describe. Drop back to "All" instead.
  // facets.js rebuilds the URL from its own forms, so it drops ?tab= on its own.
  watchFacets() {
    document.querySelectorAll('facet-filters-form form').forEach((form) => {
      form.addEventListener('input', () => this.markActive(''));
    });
  }

  /*
    facets.js listens to popstate too, and on an entry it does not recognise it refetches
    the grid for this page's own collection — which would stomp the tab we just restored.
    It bails out when the entry's searchParams match the ones it last rendered, so stamp
    those onto our state and it leaves our entries alone.
  */
  historyState(handle) {
    const state = { tab: handle };
    // Declared with `class`, so it is a lexical global — not a property of window.
    const facets = typeof FacetFiltersForm === 'undefined' ? null : FacetFiltersForm;
    if (facets) state.searchParams = facets.searchParamsPrev;
    return state;
  }

  writeUrl(handle, replace) {
    const params = new URLSearchParams(window.location.search);
    if (handle) {
      params.set('tab', handle);
    } else {
      params.delete('tab');
    }
    const query = params.toString();
    const url = `${window.location.pathname}${query ? `?${query}` : ''}`;
    const method = replace ? 'replaceState' : 'pushState';
    history[method](this.historyState(handle), '', url);
  }

  applyHandle(handle, updateUrl) {
    const url = handle ? `/collections/${handle}?section_id=${this.sectionId}` : `${this.baseUrl}?section_id=${this.sectionId}`;

    this.markActive(handle);
    if (updateUrl) this.writeUrl(handle, false);
    this.setLoading(true);

    const cached = this.cache.get(url);
    if (cached) {
      this.render(cached);
      return;
    }

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        this.cache.set(url, html);
        this.render(html);
      })
      .catch(() => {
        // Leave whatever is on screen rather than blanking the grid.
        this.setLoading(false);
      });
  }

  render(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const source = parsed.getElementById('ProductGridContainer');
    if (!source || !this.grid) {
      this.setLoading(false);
      return;
    }

    this.grid.innerHTML = source.innerHTML;
    this.grid.querySelectorAll('.scroll-trigger').forEach((el) => el.classList.add('scroll-trigger--cancel'));

    const count = parsed.getElementById('ProductCount');
    ['ProductCount', 'ProductCountDesktop'].forEach((id) => {
      const target = document.getElementById(id);
      if (target && count) target.innerHTML = count.innerHTML;
    });

    this.setLoading(false);
  }

  setLoading(isLoading) {
    const collection = this.grid && this.grid.querySelector('.collection');
    if (collection) collection.classList.toggle('loading', isLoading);
    this.root.classList.toggle('collection-tabs--loading', isLoading);
  }
}

document.querySelectorAll('.collection-tabs').forEach((root) => new CollectionTabs(root));
